import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import { capitalize, compact } from 'lodash-es';
import { Accounting } from 'anchor-scout-common/types';
import { RichTextViewer, TextButton, useOverlayContext } from 'seagull';
import { AgreementsLine, AttachFile, VendorLine24, ProfileCircleLine, EditLine, InvoicesLine, MailSentLine18, PaymentsLine, PlayIcon } from 'seagull/icons';
import { getAllowManualPaymentsChangeText, getAmendmentsApprovalMethodChangeBullet, getCoverCCFeesChangeText, getInvoicesApprovalMethodChangeBullet } from '~/business-logic/amendmentsLogic';
import { ClientPreview } from '~/components/ClientPreview/ClientPreview';
import { getURLWithFullPageInvoice } from '~/components/FullPageInvoice/FullPageInvoice.utils';
import { routes } from '~/constants';
import { netTermsToString } from '~/infra/utils';
import { ActivityLogAgreementState, ActivityLogChargeState, ActivityLogInvoiceState, ActivityLogPMIntegrationName, ActivityLogPaymentMethodType, ActivityLogServiceState, relationshipModel, serviceBillingModel, serviceCostModel, serviceModel, UserRole } from '~/models';
import { utils } from '~/utils';
import { preapprovalPeriodWordMap, recurrencePeriodWordMap } from '~/utils/billing-period-text';
import { currencyUtils } from '~/utils/currency.utils';
import pluralize from '~/utils/pluralize';
export const activityLogTextsPath = 'activityLog';
export var Placeholder = /*#__PURE__*/ function(Placeholder) {
    Placeholder["InvoiceLink"] = "[invoiceLink]";
    Placeholder["AgreementLink"] = "[agreementLink]";
    Placeholder["AgreementLinkUppercase"] = "[agreementLinkUppercase]";
    Placeholder["ManagePaymentDetails"] = "[managePaymentDetails]";
    Placeholder["ServiceDescription"] = "[serviceDescription]";
    Placeholder["OriginalService"] = "[originalService]";
    return Placeholder;
}({});
function isAttachmentEntry(entry) {
    const state = entry.state();
    return state === ActivityLogInvoiceState.InvoiceAttachmentsAdded || state === ActivityLogInvoiceState.InvoiceAttachmentRemoved;
}
export function getIcon(entry) {
    return entry.accept({
        whenAgreement: (entry)=>entry.isReminderEntry() ? /*#__PURE__*/ _jsx(MailSentLine18, {}) : /*#__PURE__*/ _jsx(AgreementsLine, {}),
        whenCharge: ()=>/*#__PURE__*/ _jsx(PaymentsLine, {}),
        whenInvoice: (entry)=>isAttachmentEntry(entry) ? /*#__PURE__*/ _jsx(AttachFile, {}) : /*#__PURE__*/ _jsx(InvoicesLine, {}),
        whenService: (entry)=>entry.isDiscountExpirationEntry() ? /*#__PURE__*/ _jsx(PlayIcon, {}) : /*#__PURE__*/ _jsx(EditLine, {}),
        whenBusiness: (entry)=>entry.isBusinessNameChanged() ? /*#__PURE__*/ _jsx(VendorLine24, {}) : /*#__PURE__*/ _jsx(ProfileCircleLine, {})
    });
}
export function getContent(config) {
    const textContent = getContentText(config);
    return replacePlaceholders(textContent, [
        [
            "[agreementLink]",
            ()=>/*#__PURE__*/ _jsx(AgreementLink, {
                    config: config
                }, "agreementLink")
        ],
        [
            "[agreementLinkUppercase]",
            ()=>/*#__PURE__*/ _jsx(AgreementLink, {
                    uppercase: true,
                    config: config
                }, "agreementLinkUppercase")
        ],
        [
            "[managePaymentDetails]",
            ()=>/*#__PURE__*/ _jsx(TextButton, {
                    as: Link,
                    to: routes.paymentMethods(config.relationship.id),
                    testId: config.testId && "".concat(config.testId, "-pm-link"),
                    underline: "none",
                    children: config.activityLogTexts.common.managePaymentDetails()
                }, "managePaymentDetails")
        ],
        [
            "[invoiceLink]",
            ()=>{
                const { relationship, testId, entry, logBI } = config;
                return entry.isInvoiceEntry() && /*#__PURE__*/ _jsx(TextButton, {
                    as: Link,
                    to: getURLWithFullPageInvoice({
                        invoiceId: entry.invoiceId(),
                        relationshipId: relationship.id
                    }),
                    onClick: ()=>logBI(10575, {
                            invoiceId: entry.invoiceId()
                        }),
                    underline: "none",
                    testId: testId && "".concat(testId, "-invoice-link"),
                    children: /*#__PURE__*/ _jsx("span", {
                        children: entry.formattedNumber()
                    })
                }, "invoiceLink");
            }
        ],
        [
            "[serviceDescription]",
            ()=>{
                const { entry, testId } = config;
                return entry.isServiceEntry() && /*#__PURE__*/ _jsx(RichTextViewer, {
                    testId: testId && "".concat(testId, "-service-description"),
                    value: entry.descriptionChange() || undefined
                });
            }
        ],
        [
            "[originalService]",
            ()=>/*#__PURE__*/ _jsx(OriginalProposalLink, {
                    config: config
                })
        ]
    ]);
}
export function getContentText(config) {
    const { entry } = config;
    return entry.accept({
        whenAgreement: (entry)=>getAgreementContent(config, entry),
        whenCharge: (entry)=>getChargeContent(config, entry),
        whenInvoice: (entry)=>getInvoiceContent(config, entry),
        whenService: (entry)=>getServiceContent(config, entry),
        whenBusiness: (entry)=>getBusinessContent(config.activityLogTexts.business, entry)
    });
}
const userDetails = (user)=>"".concat(user.name).concat(user.role === UserRole.Accountant ? " (".concat(user.email, ")") : '');
function getAgreementContent(config, entry) {
    const { isVendor } = config;
    const texts = config.activityLogTexts.agreement;
    const vendorName = entry.vendorName() || '';
    const customer = entry.customer();
    const vendor = entry.vendor();
    const customerName = entry.customerName() || '';
    const paymentMethod = entry.paymentMethod();
    const customerBusinessName = relationshipModel.customerName(config.relationship);
    const channel = entry.channel();
    switch(entry.state()){
        case ActivityLogAgreementState.Sent:
            {
                const originalService = isVendor ? "[originalService]" : '';
                if (entry.hasReviewers()) {
                    return texts.sentWithReviewers({
                        agreementLink: "[agreementLinkUppercase]",
                        customerNameAndEmail: entry.customerNameAndEmail(),
                        reviewerNames: entry.reviewerNamesAndEmails().join(', '),
                        originalService
                    });
                }
                return texts.sent({
                    agreementLink: "[agreementLinkUppercase]",
                    customerNameAndEmail: entry.customerNameAndEmail(),
                    originalService
                });
            }
        case ActivityLogAgreementState.Reviewed:
            return texts.reviewed({
                agreementLink: "[agreementLinkUppercase]",
                customerName
            });
        case ActivityLogAgreementState.MicroDepositStarted:
        case ActivityLogAgreementState.MicroDeposit:
            return texts.microDepositInitiated({
                name: customerName
            });
        case ActivityLogAgreementState.MicroDepositStartedV2:
            return texts.microDepositStartedV2({
                last4: (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****'
            });
        case ActivityLogAgreementState.MicroDepositFailed:
            return texts.microDepositFailed();
        case ActivityLogAgreementState.MicroDepositFailedV2:
            return texts.microDepositFailedV2({
                last4: (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****',
                managePaymentDetails: "[managePaymentDetails]"
            });
        case ActivityLogAgreementState.MicroDepositVerified:
            return texts.microDepositVerified({
                name: customerName || vendorName
            });
        case ActivityLogAgreementState.MicroDepositVerifiedV2:
            return texts.microDepositVerifiedV2({
                last4: (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****'
            });
        case ActivityLogAgreementState.PaymentMethod:
            return texts.paymentMethod({
                name: customerName || vendorName
            });
        case ActivityLogAgreementState.Approved:
            return texts.approved({
                agreementLink: "[agreementLinkUppercase]",
                customerName
            });
        case ActivityLogAgreementState.Terminated:
            return texts.terminated({
                agreementLink: "[agreementLinkUppercase]",
                vendorName
            });
        case ActivityLogAgreementState.Changed:
            {
                const netTerms = entry.netTermsChanges();
                const netTermsText = netTerms != null ? texts.paymentTerms({
                    value: netTermsToString(netTerms)
                }) : '';
                return texts.changed({
                    agreementLink: "[agreementLinkUppercase]",
                    vendorName,
                    details: netTermsText
                });
            }
        case ActivityLogAgreementState.ChangesApproved:
            return texts.changesApproved({
                customerName
            });
        case ActivityLogAgreementState.ChangesAutoApproved:
            return texts.changesAutoApproved();
        case ActivityLogAgreementState.StoppedAutoApproval:
            return texts.stoppedAutoApproval({
                customerName
            });
        case ActivityLogAgreementState.SigningReminderSent:
            return texts.signingReminderSent({
                channel,
                customerName
            });
        case ActivityLogAgreementState.SigningReminderSentManual:
            return texts.signingReminderSentManual({
                vendorName,
                channel,
                customerName
            });
        case ActivityLogAgreementState.MicroDepositReminderSent:
            return texts.microDepositReminderSent({
                customerName
            });
        case ActivityLogAgreementState.PaymentMethodChanged:
            {
                const commonTexts = config.activityLogTexts.common;
                const user = customer || vendor;
                if (!paymentMethod || !user) {
                    return null;
                }
                const performedByVendor = !customer || user.role === UserRole.Accountant;
                const pmType = paymentMethod.type === ActivityLogPaymentMethodType.CreditCard ? commonTexts.creditCard() : commonTexts.bankAccount();
                const params = {
                    agreementLink: "[agreementLink]",
                    pmType,
                    last4: paymentMethod.last4,
                    userDetails: userDetails(user)
                };
                return performedByVendor ? texts.paymentMethodChangedWithAuthorization(_object_spread_props(_object_spread({}, params), {
                    customerBusinessName
                })) : texts.paymentMethodChanged(params);
            }
        case ActivityLogAgreementState.PaymentMethodDisconnected:
            {
                const commonTexts = config.activityLogTexts.common;
                if (!paymentMethod) {
                    return null;
                }
                const pmType = paymentMethod.type === ActivityLogPaymentMethodType.CreditCard ? commonTexts.creditCardLower() : commonTexts.bankAccountLower();
                return texts.paymentMethodDisconnected({
                    pmType,
                    last4: paymentMethod.last4,
                    userDetails: vendor ? userDetails(vendor) : ''
                });
            }
        case ActivityLogAgreementState.SignedExternally:
            return texts.signedExternally({
                vendorName
            });
        case ActivityLogAgreementState.RequestedPaymentMethod:
            {
                if (!customer) return null;
                return texts.requestedPaymentMethod({
                    customerName: customer.name,
                    customerEmail: customer.email
                });
            }
        case ActivityLogAgreementState.LegalTermsUpdated:
            return texts.legalTermsUpdated();
        case ActivityLogAgreementState.AmendmentsApprovalMethodUpdated:
            {
                const approvalMethodChanges = entry.amendmentsApprovalMethodChanges();
                if (!approvalMethodChanges) return null;
                const bullet = getAmendmentsApprovalMethodChangeBullet(approvalMethodChanges.before, approvalMethodChanges.after);
                if (!bullet) return null;
                return vendorName ? texts.approvalMethodUpdatedByVendor({
                    bulletText: bullet.text,
                    vendorName
                }) : texts.approvalMethodUpdated({
                    bulletText: bullet.text
                });
            }
        case ActivityLogAgreementState.InvoicesApprovalMethodUpdated:
            {
                const approvalMethodChanges = entry.invoicesApprovalMethodChanges();
                if (!approvalMethodChanges) return null;
                const bullet = getInvoicesApprovalMethodChangeBullet(approvalMethodChanges.before, approvalMethodChanges.after);
                if (!bullet) return null;
                return vendorName ? texts.approvalMethodUpdatedByVendor({
                    bulletText: bullet.text,
                    vendorName
                }) : texts.approvalMethodUpdated({
                    bulletText: bullet.text
                });
            }
        case ActivityLogAgreementState.TitleUpdated:
            {
                const changesTitle = entry.titleChanges();
                return changesTitle ? texts.titleUpdated({
                    vendorName,
                    agreementLink: "[agreementLink]",
                    title: changesTitle.after
                }) : null;
            }
        case ActivityLogAgreementState.ManualPaymentsUpdated:
            {
                const changesManualPayments = entry.manualPaymentsChanges();
                if (!changesManualPayments) return null;
                const { before, after } = changesManualPayments;
                return texts.manualPaymentsUpdated({
                    changeText: getAllowManualPaymentsChangeText(before, after),
                    vendorName
                });
            }
        case ActivityLogAgreementState.CoverCreditCardFeesUpdated:
            {
                const changesCoverCCFees = entry.coverCCFeesChanges();
                if (!changesCoverCCFees) return null;
                const changeText = getCoverCCFeesChangeText(changesCoverCCFees.before, changesCoverCCFees.after, relationshipModel.vendorName(config.relationship), relationshipModel.customerName(config.relationship));
                return texts.coverCreditCardFeesUpdated({
                    changeText,
                    vendorName
                });
            }
        case ActivityLogAgreementState.SellerUpdated:
            {
                const changesSeller = entry.sellerChanges();
                if (!changesSeller) return null;
                return texts.sellerUpdated({
                    before: changesSeller.before,
                    after: changesSeller.after,
                    vendorName
                });
            }
        case ActivityLogAgreementState.PracticeManagementContactCreated:
            {
                const integrationName = entry.integrationName();
                return integrationName ? texts.practiceManagementContactCreated({
                    integrationName: integrationNameToText[integrationName]
                }) : null;
            }
        case ActivityLogAgreementState.PracticeManagementItemsCreated:
            {
                const integrationName = entry.integrationName();
                const integrationServices = entry.integrationServices();
                return integrationName && (integrationServices === null || integrationServices === void 0 ? void 0 : integrationServices.length) ? [
                    texts.practiceManagementItemsCreated({
                        integrationName: integrationNameToText[integrationName]
                    }) + '\n'
                ].concat(integrationServices.map((service)=>"• ".concat(service.name))).join('\n') : null;
            }
        case ActivityLogAgreementState.SentSilently:
            {
                return texts.sentSilently({
                    vendorName,
                    originalService: isVendor ? "[originalService]" : ''
                });
            }
        case ActivityLogAgreementState.MicroDepositRestarted:
            {
                return texts.microDepositRestarted({
                    last4: (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****',
                    name: customerName || vendorName
                });
            }
        case ActivityLogAgreementState.AmendmentApprovalReminderSent:
            {
                return texts.amendmentApprovalReminderSent({
                    customerBusinessName
                });
            }
        default:
            return null;
    }
}
const integrationNameToText = {
    [ActivityLogPMIntegrationName.Karbon]: 'Karbon',
    [ActivityLogPMIntegrationName.ClientHub]: 'Client Hub',
    [ActivityLogPMIntegrationName.FinancialCents]: 'Financial Cents',
    [ActivityLogPMIntegrationName.Keeper]: 'Double'
};
function getChargeContent(config, entry) {
    const texts = config.activityLogTexts.charge;
    const chargeName = entry.name();
    const vendorName = entry.vendorName() || '';
    const customerName = entry.customerName() || '';
    switch(entry.state()){
        case ActivityLogChargeState.Created:
            {
                const chargeNote = entry.note();
                const params = {
                    chargeName,
                    price: entry.formattedPrice(),
                    vendorName
                };
                return chargeNote ? texts.createdWithNote(_object_spread_props(_object_spread({}, params), {
                    note: chargeNote
                })) : texts.created(params);
            }
        case ActivityLogChargeState.Approved:
            return texts.approved({
                chargeName,
                price: entry.formattedPrice(),
                customerName
            });
        case ActivityLogChargeState.Canceled:
            return texts.canceled({
                chargeName,
                price: entry.formattedPrice(),
                vendorName
            });
        default:
            return null;
    }
}
function getInvoiceContent(config, entry) {
    const { relationship, isVendor } = config;
    const texts = config.activityLogTexts.invoice;
    const vendorName = entry.vendorName() || '';
    const customerName = entry.customerName() || '';
    const vendorBusinessName = relationshipModel.vendorName(relationship);
    const customerBusinessName = relationshipModel.customerName(relationship);
    switch(entry.state()){
        case ActivityLogInvoiceState.AccountingInvoiceResyncRequested:
            return entry.integrationType() === Accounting.Xero ? texts.accountingInvoiceResyncRequestedXero({
                invoiceLink: "[invoiceLink]",
                integrationName: entry.integrationFullName(),
                vendorName
            }) : texts.accountingInvoiceResyncRequested({
                invoiceLink: "[invoiceLink]",
                integrationName: entry.integrationFullName(),
                vendorName
            });
        case ActivityLogInvoiceState.AccountingInvoiceSynced:
            return texts.accountingInvoiceSynced({
                invoiceLink: "[invoiceLink]",
                integrationName: entry.integrationFullName()
            });
        case ActivityLogInvoiceState.AccountingPaymentSynced:
            return texts.accountingPaymentSynced({
                invoiceLink: "[invoiceLink]",
                integrationName: entry.integrationFullName()
            });
        case ActivityLogInvoiceState.Voided:
            return texts.voided({
                invoiceLink: "[invoiceLink]",
                vendorName
            });
        case ActivityLogInvoiceState.PaymentStoppedOLD:
            return texts.paymentStoppedOld({
                invoiceLink: "[invoiceLink]",
                customerName
            });
        case ActivityLogInvoiceState.PaymentStopped:
            return texts.paymentStopped({
                invoiceLink: "[invoiceLink]",
                customerName,
                customerBusinessName,
                reason: entry.paymentStoppedReason() || ''
            });
        case ActivityLogInvoiceState.PaymentResumed:
            return texts.paymentResumed({
                invoiceLink: "[invoiceLink]",
                customerName,
                customerBusinessName
            });
        case ActivityLogInvoiceState.Completed:
            return texts.completed({
                invoiceLink: "[invoiceLink]",
                vendorName
            });
        case ActivityLogInvoiceState.IssuedIncomplete:
            return texts.issuedIncomplete({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.Issued:
            return texts.issued({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.PaymentFailed:
            return texts.paymentFailed({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.PaymentRetried:
            return texts.paymentRetried({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.PaymentRetriedByAdmin:
            return texts.paymentRetriedByAdmin({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.PaymentAutoRetried:
            return texts.paymentAutoRetried({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.PaymentRetriedByCustomer:
            return texts.paymentRetriedByCustomer({
                invoiceLink: "[invoiceLink]",
                customerName,
                customerBusinessName
            });
        case ActivityLogInvoiceState.PaymentRetriedByVendor:
            return texts.paymentRetriedByVendor({
                invoiceLink: "[invoiceLink]",
                vendorName,
                vendorBusinessName
            });
        case ActivityLogInvoiceState.Paid:
            return texts.paid({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.PaidExternally:
            return texts.paidExternally({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.DisputeResolved:
            return texts.disputeResolved({
                invoiceLink: "[invoiceLink]",
                customerName,
                customerBusinessName
            });
        case ActivityLogInvoiceState.Approved:
            return texts.approved({
                invoiceLink: "[invoiceLink]",
                customerName
            });
        case ActivityLogInvoiceState.PaymentAutoApproved:
            return texts.paymentAutoApproved({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.AutoApprovalDisabled:
            return texts.autoApprovalDisabled({
                invoiceLink: "[invoiceLink]",
                customerName
            });
        case ActivityLogInvoiceState.Resent:
            return texts.resent({
                invoiceLink: "[invoiceLink]",
                vendorName
            });
        case ActivityLogInvoiceState.PaymentConfirmed:
            return texts.paymentConfirmed({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.PaymentProcessing:
            return texts.paymentProcessing({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.Refunded:
            {
                if (entry.isFullRefund()) {
                    if (isVendor) return texts.refundedFullVendor({
                        vendorName,
                        amount: entry.totalAmount().display(),
                        invoiceLink: "[invoiceLink]",
                        reason: entry.refundRequestedReason() || ''
                    });
                    return texts.refundedFull({
                        vendorName,
                        amount: entry.totalAmount().display(),
                        invoiceLink: "[invoiceLink]"
                    });
                } else if (entry.haveFees()) {
                    if (isVendor) return texts.refundedPartialWithFeesVendor({
                        vendorName,
                        amount: entry.totalRefundedAmount().display(),
                        invoiceLink: "[invoiceLink]",
                        reason: entry.refundRequestedReason() || ''
                    });
                    return texts.refundedPartialWithFees({
                        vendorName,
                        amount: entry.totalRefundedAmount().display(),
                        invoiceLink: "[invoiceLink]"
                    });
                } else {
                    if (isVendor) return texts.refundedPartialVendor({
                        vendorName,
                        amount: entry.totalRefundedAmount().display(),
                        invoiceLink: "[invoiceLink]",
                        reason: entry.refundRequestedReason() || ''
                    });
                    return texts.refundedPartial({
                        vendorName,
                        amount: entry.totalRefundedAmount().display(),
                        invoiceLink: "[invoiceLink]"
                    });
                }
            }
        case ActivityLogInvoiceState.PaymentDisputed:
            return texts.paymentDisputed({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.PayNowRequested:
            return texts.payNowRequested({
                invoiceLink: "[invoiceLink]",
                customerName
            });
        case ActivityLogInvoiceState.RefundRequested:
            return null;
        case ActivityLogInvoiceState.RefundFailed:
            {
                if (entry.isFullRefund()) {
                    return texts.refundFailed({
                        invoiceLink: "[invoiceLink]",
                        amount: entry.totalAmount().display()
                    });
                } else if (entry.haveFees()) {
                    return texts.refundFailedPartialWithFees({
                        invoiceLink: "[invoiceLink]",
                        amount: entry.totalRefundedAmount().display()
                    });
                } else {
                    return texts.refundFailedPartial({
                        invoiceLink: "[invoiceLink]",
                        amount: entry.totalRefundedAmount().display()
                    });
                }
            }
        case ActivityLogInvoiceState.InvoiceSanctionFound:
            return texts.invoiceSanctionFound({
                invoiceLink: "[invoiceLink]"
            });
        case ActivityLogInvoiceState.Unvoided:
            return texts.unvoided({
                invoiceLink: "[invoiceLink]",
                vendorName
            });
        case ActivityLogInvoiceState.UnmarkedAsPaidExternally:
            return texts.unmarkedAsPaidExternally({
                invoiceLink: "[invoiceLink]",
                vendorName
            });
        case ActivityLogInvoiceState.InvoiceAttachmentsAdded:
            {
                const { displayNames, userName, userEmail } = entry.attachmentsAddedInfo();
                return texts.attachmentsAdded({
                    invoiceLink: "[invoiceLink]",
                    count: displayNames.length,
                    fileNames: displayNames.join(', '),
                    updatedBy: "".concat(userName, " (").concat(userEmail, ")")
                });
            }
        case ActivityLogInvoiceState.InvoiceAttachmentRemoved:
            {
                const { displayName, userName, userEmail } = entry.attachmentRemovedInfo();
                return texts.attachmentRemoved({
                    invoiceLink: "[invoiceLink]",
                    fileName: displayName,
                    updatedBy: "".concat(userName, " (").concat(userEmail, ")")
                });
            }
        default:
            return null;
    }
}
function getServiceContent(config, entry) {
    const texts = config.activityLogTexts.service;
    const helpers = texts.helpers;
    const vendorName = entry.vendorName() || '';
    const customerName = entry.customerName() || '';
    const serviceName = entry.name();
    switch(entry.state()){
        case ActivityLogServiceState.Approved:
            return texts.approved({
                serviceName,
                customerName
            });
        case ActivityLogServiceState.ChangesApproved:
            return texts.changesApproved({
                serviceName,
                customerName
            });
        case ActivityLogServiceState.ChangesAutoApproved:
            return texts.changesAutoApproved({
                serviceName
            });
        case ActivityLogServiceState.Resumed:
            return texts.resumed({
                vendorName,
                serviceName
            });
        case ActivityLogServiceState.Paused:
            return texts.paused({
                vendorName,
                serviceName
            });
        case ActivityLogServiceState.Created:
            {
                const serviceChangesCost = entry.serviceChangesCost();
                return compact([
                    texts.created({
                        vendorName,
                        serviceName
                    }),
                    getServiceDescription(helpers, entry.descriptionChange()),
                    getServicePricePerUnitText(helpers, serviceChangesCost),
                    getServiceDiscountText(helpers, serviceChangesCost),
                    getServiceCap(helpers, serviceChangesCost, entry.billingChange()),
                    getServiceRecurrenceText(helpers, entry),
                    getServiceNote(helpers, entry.note())
                ]).join('\n');
            }
        case ActivityLogServiceState.Changed:
            {
                const serviceChangesCost = entry.serviceChangesCost();
                return compact([
                    texts.changed({
                        vendorName,
                        serviceName
                    }),
                    getServiceDescription(helpers, entry.descriptionChange()),
                    getServicePricePerUnitText(helpers, serviceChangesCost),
                    getServiceDiscountText(helpers, serviceChangesCost),
                    getServiceCap(helpers, serviceChangesCost, entry.billingChange()),
                    getServiceRecurrenceText(helpers, entry),
                    getServiceNote(helpers, entry.note())
                ]).join('\n');
            }
        case ActivityLogServiceState.Terminated:
            return texts.terminated({
                vendorName,
                serviceName
            });
        case ActivityLogServiceState.Canceled:
            return texts.canceled({
                vendorName,
                serviceName
            });
        case ActivityLogServiceState.BillCommandDeliverablesPendingApproval:
            return texts.billCommandDeliverablesPendingApproval({
                vendorName,
                serviceName
            });
        case ActivityLogServiceState.BillCommandDeliverablesApproved:
            return texts.billCommandDeliverablesApproved({
                customerName,
                serviceName
            });
        case ActivityLogServiceState.BillCommandDeliverablesRejected:
            return texts.billCommandDeliverablesRejected({
                customerName,
                serviceName
            });
        case ActivityLogServiceState.BillCommandDeliverablesWithdrawn:
            return texts.billCommandDeliverablesWithdrawn({
                vendorName,
                serviceName
            });
        case ActivityLogServiceState.ChangesDiscarded:
            return texts.changesDiscarded({
                vendorName,
                serviceName
            });
        case ActivityLogServiceState.PriceIncreased:
            {
                const changesPrice = entry.priceChange();
                const beforeChangesPrice = entry.priceBeforeChange();
                return changesPrice && beforeChangesPrice ? texts.priceIncreased({
                    serviceName,
                    beforePrice: currencyUtils.formatDollarAmount(beforeChangesPrice),
                    afterPrice: currencyUtils.formatDollarAmount(changesPrice)
                }) : null;
            }
        case ActivityLogServiceState.IntegratedClassChanged:
            {
                const defaultText = 'the default';
                const before = entry.integratedClassNameBeforeChange() || defaultText;
                const after = entry.integratedClassNameChange() || defaultText;
                return texts.integratedClassChanged({
                    serviceName,
                    before,
                    after,
                    vendorName
                });
            }
        case ActivityLogServiceState.ServiceDiscountExpired:
            {
                return texts.discountExpired({
                    serviceName
                });
            }
        default:
            return null;
    }
}
function formatNameAndEmail(name, email) {
    return "".concat(name, " (").concat(email, ")");
}
export function getBusinessContent(texts, entry) {
    return entry.acceptBusinessEntry({
        whenBusinessNameChanged: (entry)=>{
            const updatedBy = formatNameAndEmail(entry.updatedByName(), entry.updatedByEmail());
            return texts.businessNameChanged({
                newName: entry.newName(),
                updatedBy
            });
        },
        whenCustomerNameChangedBySeller: (entry)=>{
            const updatedBy = formatNameAndEmail(entry.updatedByName(), entry.updatedByEmail());
            return texts.customerNameChangedBySeller({
                newName: entry.newName(),
                updatedBy,
                vendorName: entry.vendorName()
            });
        },
        whenTeamMemberAdded: (entry)=>{
            const member = formatNameAndEmail(entry.addedMemberName(), entry.addedMemberEmail());
            const updatedBy = formatNameAndEmail(entry.updatedByName(), entry.updatedByEmail());
            return texts.teamMemberAdded({
                member,
                role: capitalize(entry.addedMemberRole()),
                updatedBy
            });
        },
        whenTeamMemberAddedBySeller: (entry)=>{
            const member = formatNameAndEmail(entry.addedMemberName(), entry.addedMemberEmail());
            const updatedBy = formatNameAndEmail(entry.updatedByName(), entry.updatedByEmail());
            return texts.teamMemberAddedBySeller({
                member,
                role: capitalize(entry.addedMemberRole()),
                updatedBy,
                vendorName: entry.vendorName()
            });
        },
        whenTeamMemberRemoved: (entry)=>{
            const member = formatNameAndEmail(entry.removedMemberName(), entry.removedMemberEmail());
            const updatedBy = formatNameAndEmail(entry.updatedByName(), entry.updatedByEmail());
            return texts.teamMemberRemoved({
                member,
                updatedBy
            });
        },
        whenTeamMemberRemovedBySeller: (entry)=>{
            const member = formatNameAndEmail(entry.removedMemberName(), entry.removedMemberEmail());
            const updatedBy = formatNameAndEmail(entry.updatedByName(), entry.updatedByEmail());
            return texts.teamMemberRemovedBySeller({
                member,
                updatedBy,
                vendorName: entry.vendorName()
            });
        },
        whenTeamMemberRoleChanged: (entry)=>{
            const member = formatNameAndEmail(entry.memberName(), entry.memberEmail());
            const updatedBy = formatNameAndEmail(entry.updatedByName(), entry.updatedByEmail());
            return texts.teamMemberRoleChanged({
                member,
                role: capitalize(entry.newRole()),
                updatedBy
            });
        },
        whenTeamMemberRoleChangedBySeller: (entry)=>{
            const member = formatNameAndEmail(entry.memberName(), entry.memberEmail());
            const updatedBy = formatNameAndEmail(entry.updatedByName(), entry.updatedByEmail());
            return texts.teamMemberRoleChangedBySeller({
                member,
                role: capitalize(entry.newRole()),
                updatedBy,
                vendorName: entry.vendorName()
            });
        },
        whenCustomerBusinessCreated: (entry)=>{
            const createdBy = formatNameAndEmail(entry.createdByName(), entry.createdByEmail());
            return texts.customerBusinessCreated({
                businessName: entry.businessName(),
                createdBy,
                vendorName: entry.vendorName()
            });
        }
    });
}
function replacePlaceholders(content, replacers) {
    if (!content) {
        return null;
    }
    let result = [
        content
    ];
    replacers.forEach((replacer)=>{
        result = replacePlaceholder(result, replacer);
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: result
    });
}
function replacePlaceholder(content, param) {
    let [placeholder, getReplacedContent] = param;
    return content.map((item)=>{
        if (typeof item !== 'string') {
            return item;
        }
        const splitted = item.split(placeholder);
        if (splitted.length === 1) {
            return item;
        }
        const result = [];
        splitted.forEach((item, index)=>{
            result.push(item);
            if (index < splitted.length - 1) {
                result.push(getReplacedContent());
            }
        });
        return compact(result);
    }).flat(1);
}
function getServiceDescription(helpers, description) {
    return description ? "".concat(helpers.description(), "\n", "[serviceDescription]") : '';
}
function getServiceNote(helpers, note) {
    return note ? helpers.note({
        note
    }) : '';
}
function getServiceRecurrenceText(helpers, entry) {
    if (!entry.hasChanges()) {
        return '';
    }
    const billing = entry.billingChange();
    const isRecurring = !!billing && serviceBillingModel.isRecurring(billing);
    if (!isRecurring) {
        return helpers.recurrenceOneTime();
    }
    const period = serviceModel.recurrencePeriod(billing);
    const dayOfWeek = billing ? serviceBillingModel.billingDayOfWeek(billing) : null;
    const dayOfMonth = billing ? serviceBillingModel.billingDayOfMonth(billing) : null;
    if (dayOfWeek) {
        return helpers.recurrenceOngoingWithDayOfWeek({
            period,
            dayOfWeek
        });
    }
    if (dayOfMonth) {
        return helpers.recurrenceOngoingWithDayOfMonth({
            period,
            dayOfMonth: dayOfMonth.formatDay('last day')
        });
    }
    return helpers.recurrenceOngoing({
        period
    });
}
function getServicePricePerUnitText(helpers, cost) {
    const price = cost && serviceModel.price({
        cost
    });
    if (price) {
        const formattedPrice = utils.formatDollarAmount(price);
        if (serviceCostModel.isVariablePrice(cost)) {
            return helpers.priceVariable({
                price: formattedPrice,
                unitName: serviceCostModel.formattedUnitName(cost)
            });
        } else if (serviceCostModel.isRangePrice(cost)) {
            const minPrice = serviceModel.minPrice({
                cost
            });
            return helpers.priceRange({
                minPrice: minPrice ? utils.formatDollarAmount(minPrice) : '0',
                maxPrice: formattedPrice
            });
        } else {
            return helpers.priceFixed({
                price: formattedPrice
            });
        }
    } else {
        return '';
    }
}
function getServiceDiscountText(helpers, cost) {
    const discount = cost && serviceModel.discount({
        cost
    });
    return discount ? helpers.discount({
        discount: discount.format()
    }) : '';
}
function billingPeriodSuffix(billing) {
    if (!billing) return '';
    if (serviceBillingModel.isOnDemand(billing)) {
        const word = preapprovalPeriodWordMap[billing.preapprovalPeriod];
        return word ? "/".concat(word) : '';
    }
    if (serviceBillingModel.isRecurring(billing)) {
        const word = recurrencePeriodWordMap[billing.recurrencePeriod];
        return word ? "/".concat(word) : '';
    }
    return '';
}
function getServiceCap(helpers, cost, billing) {
    const unitCap = cost && serviceModel.unitCap({
        cost
    });
    return unitCap ? helpers.preapproved({
        count: utils.formatNumberWithCommas(unitCap),
        units: pluralize(serviceCostModel.formattedUnitName(cost), unitCap.toNumber()) + billingPeriodSuffix(billing)
    }) : '';
}
function AgreementLink(props) {
    const { uppercase, config: { relationship, logBI, testId, activityLogTexts } } = props;
    return /*#__PURE__*/ _jsx(TextButton, {
        as: Link,
        to: routes.agreement(relationship.id),
        onClick: ()=>logBI(10576),
        testId: testId && "".concat(testId, "-agreement-link"),
        underline: "none",
        children: uppercase ? activityLogTexts.common.agreement() : activityLogTexts.common.agreementLower()
    });
}
const OriginalProposalLink = (props)=>{
    const { config: { relationship, testId, activityLogTexts } } = props;
    const overlayContext = useOverlayContext();
    return /*#__PURE__*/ _jsx(TextButton, {
        onClick: ()=>{
            overlayContext.showModal({
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal, registerCanDismiss } = param;
                    return /*#__PURE__*/ _jsx(ClientPreview, {
                        type: "originalProposal",
                        relationshipId: relationship.id,
                        dismissModal: dismissModal,
                        registerCanDismiss: registerCanDismiss
                    });
                }
            });
        },
        testId: testId && "".concat(testId, "-view-original-proposal"),
        underline: "none",
        children: activityLogTexts.common.viewOriginalProposal()
    });
};
